/**
 * Almacenamiento persistente de escenas eliminadas del Storyboard,
 * con persistencia principal en servidor (db.scene_trash) y respaldo en localStorage,
 * completamente aislado por usuario y proyecto.
 *
 * Incluye registro local mínimo de escenas eliminadas definitivamente (tombstones)
 * para impedir que copias locales antiguas o pestañas desactualizadas resuciten
 * escenas purgadas.
 */

const STORAGE_PREFIX = "veyra_scene_trash";
const PERM_DELETED_PREFIX = "veyra_perm_deleted_scenes";

export function getTrashStorageKey(userId, projectId) {
  const uid = userId || "guest";
  const pid = projectId || "global";
  return `${STORAGE_PREFIX}:${uid}:${pid}`;
}

export function getPermDeletedStorageKey(userId, projectId) {
  const uid = userId || "guest";
  const pid = projectId || "global";
  return `${PERM_DELETED_PREFIX}:${uid}:${pid}`;
}

export function getPermanentlyDeletedSceneIds(userId, projectId) {
  try {
    const raw = localStorage.getItem(getPermDeletedStorageKey(userId, projectId));
    if (!raw) return new Set();
    const list = JSON.parse(raw);
    return new Set(Array.isArray(list) ? list : []);
  } catch (err) {
    console.warn("Error reading perm deleted scene ids:", err);
    return new Set();
  }
}

export function recordPermanentlyDeletedScenes(userId, projectId, sceneIds) {
  if (!Array.isArray(sceneIds) || sceneIds.length === 0) return;
  const key = getPermDeletedStorageKey(userId, projectId);
  try {
    const currentSet = getPermanentlyDeletedSceneIds(userId, projectId);
    sceneIds.forEach((id) => {
      if (id && typeof id === "string") {
        currentSet.add(id);
      }
    });
    localStorage.setItem(key, JSON.stringify(Array.from(currentSet)));

    // Retirar de la papelera local inmediatamente
    const trashKey = getTrashStorageKey(userId, projectId);
    const rawTrash = localStorage.getItem(trashKey);
    if (rawTrash) {
      const list = JSON.parse(rawTrash);
      if (Array.isArray(list)) {
        const filtered = list.filter((item) => {
          const sid = item.scene?.id || item.id || item.scene_id;
          return !currentSet.has(sid);
        });
        localStorage.setItem(trashKey, JSON.stringify(filtered));
      }
    }
  } catch (err) {
    console.warn("Error recording permanently deleted scenes:", err);
  }
}

export function permanentlyDeleteFromTrash(userId, projectId, sceneIds) {
  const ids = Array.isArray(sceneIds) ? sceneIds : [sceneIds];
  recordPermanentlyDeletedScenes(userId, projectId, ids);
}

export function getTrashScenes(userId, projectId) {
  try {
    const raw = localStorage.getItem(getTrashStorageKey(userId, projectId));
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    const permDeleted = getPermanentlyDeletedSceneIds(userId, projectId);
    return list.filter((item) => {
      const sid = item.scene?.id || item.id || item.scene_id;
      return !permDeleted.has(sid);
    });
  } catch (err) {
    console.warn("Error reading local scene trash:", err);
    return [];
  }
}

export function addSceneToTrash(userId, projectId, scene, extra = {}) {
  if (!scene || !scene.id) return null;
  const permDeleted = getPermanentlyDeletedSceneIds(userId, projectId);
  if (permDeleted.has(scene.id)) {
    // Si fue eliminada definitivamente, no permitir añadirla de nuevo a la papelera
    return null;
  }

  const key = getTrashStorageKey(userId, projectId);
  try {
    const current = getTrashScenes(userId, projectId);
    // Evitar duplicados por scene.id
    const filtered = current.filter((item) => (item.scene?.id || item.id) !== scene.id);
    const trashItem = {
      id: scene.id,
      scene: { ...scene },
      originalOrder: typeof extra.originalOrder === "number" ? extra.originalOrder : (scene.order ?? 0),
      deletedAt: new Date().toISOString(),
      nodes: Array.isArray(extra.nodes) ? extra.nodes : [],
      edges: Array.isArray(extra.edges) ? extra.edges : [],
    };
    const next = [trashItem, ...filtered];
    localStorage.setItem(key, JSON.stringify(next));
    return trashItem;
  } catch (err) {
    console.warn("Error adding scene to local trash:", err);
    return null;
  }
}

export function removeSceneFromTrash(userId, projectId, sceneId) {
  if (!sceneId) return null;
  const key = getTrashStorageKey(userId, projectId);
  try {
    const current = getTrashScenes(userId, projectId);
    const target = current.find((item) => item.scene?.id === sceneId || item.id === sceneId);
    if (!target) return null;
    const next = current.filter((item) => item.scene?.id !== sceneId && item.id !== sceneId);
    localStorage.setItem(key, JSON.stringify(next));
    return target;
  } catch (err) {
    console.warn("Error removing scene from local trash:", err);
    return null;
  }
}

export function isSceneInTrash(userId, projectId, sceneId) {
  if (!sceneId) return false;
  const current = getTrashScenes(userId, projectId);
  return current.some((item) => item.scene?.id === sceneId || item.id === sceneId);
}

/**
 * Importa de forma segura y validada los ítems locales existentes al servidor.
 * REGLA ESTRICTA: La copia local NUNCA se borra hasta recibir confirmación HTTP 200 del servidor.
 */
export async function syncLocalTrashToServer(userId, projectId, apiInstance) {
  if (!apiInstance || typeof apiInstance.syncLocalTrash !== "function") {
    return { synced: 0 };
  }
  const localItems = getTrashScenes(userId, projectId);
  if (!localItems || localItems.length === 0) {
    return { synced: 0 };
  }

  try {
    const res = await apiInstance.syncLocalTrash({
      project_id: projectId,
      items: localItems,
    });

    if (res && res.ok) {
      if (Array.isArray(res.permanently_deleted_ids) && res.permanently_deleted_ids.length > 0) {
        recordPermanentlyDeletedScenes(userId, projectId, res.permanently_deleted_ids);
      }
      return { synced: res.synced || localItems.length, synced_ids: res.synced_ids || [] };
    }
    return { synced: 0 };
  } catch (err) {
    console.warn("Error al sincronizar papelera local con el servidor (se conserva la copia local):", err);
    return { synced: 0, error: err };
  }
}

/**
 * Consulta la papelera desde el servidor. Si el servidor responde, devuelve esos datos;
 * preservando cualquier respaldo local y evitando duplicados.
 */
export async function fetchServerTrash(userId, projectId, apiInstance) {
  const permDeleted = getPermanentlyDeletedSceneIds(userId, projectId);
  const localList = getTrashScenes(userId, projectId);
  if (apiInstance && typeof apiInstance.getTrashScenes === "function") {
    try {
      await syncLocalTrashToServer(userId, projectId, apiInstance).catch(() => {});
      const res = await apiInstance.getTrashScenes(projectId);
      if (res && Array.isArray(res.scenes)) {
        const filteredServerScenes = res.scenes.filter((s) => {
          const sid = s.scene?.id || s.id || s.scene_id;
          return !permDeleted.has(sid);
        });
        const serverIds = new Set(filteredServerScenes.map((s) => s.scene?.id || s.id || s.scene_id));
        const missingLocals = localList.filter((l) => !serverIds.has(l.scene?.id || l.id));
        return [...filteredServerScenes, ...missingLocals];
      }
    } catch (err) {
      console.warn("No se pudo consultar la papelera del servidor, usando respaldo local:", err);
      throw err;
    }
  }
  return localList;
}
